import { requireOrganizationId, type OrganizationId } from '@/lib/organization-scoping'
import { organizationQueryKeys } from '@/lib/services/organizations/organizations.query'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createSalesDemo,
  deactivateSalesDemo,
  extendSalesDemo,
  generateSalesDemoLoginCredentials,
  getSalesDemo,
  reactivateSalesDemo,
  retrySalesDemo,
} from './sales-demos.api'
import type { SalesDemoLifecycleResult, SalesDemoState } from './sales-demos.types'
import { isSalesDemoProvisioning } from './sales-demos.utils'

interface SalesDemoQueryRoot {
  entity: 'sales-demo'
}

/** The fetcher reads the organization id from the key; the key builder below sets it. */
export type SalesDemoShowQueryKey = [SalesDemoQueryRoot & { organizationId: number; scope: 'show' }]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

const salesDemoQueryRoot: SalesDemoQueryRoot = { entity: 'sales-demo' }

export const salesDemoQueryKeys = {
  all: [salesDemoQueryRoot] satisfies [SalesDemoQueryRoot],
  show: (organizationId: OrganizationId): SalesDemoShowQueryKey => [
    {
      ...salesDemoQueryKeys.all[0],
      organizationId: requireOrganizationId(organizationId),
      scope: 'show',
    },
  ],
}

export const salesDemoQueries = {
  show: (organizationId: OrganizationId) =>
    queryOptions({
      queryKey: salesDemoQueryKeys.show(organizationId),
      queryFn: getSalesDemo,
    }),
}

export function useSalesDemo(
  organizationId: OrganizationId,
  params: QueryParams<ReturnType<typeof salesDemoQueries.show>> = {}
) {
  return useQuery({
    ...salesDemoQueries.show(organizationId),
    refetchInterval: (query) => {
      return isSalesDemoProvisioning(query.state.data?.sales_demo) ? 2_000 : false
    },
    ...params,
  })
}

function useSalesDemoLifecycleMutation(
  mutationFn: (organizationId: OrganizationId) => Promise<SalesDemoLifecycleResult>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: async (data, organizationId) => {
      // The section only offers these actions once the demo state has loaded.
      queryClient.setQueryData<SalesDemoState>(
        salesDemoQueryKeys.show(organizationId),
        (current) => current && { ...current, sales_demo: data.sales_demo }
      )

      await queryClient.invalidateQueries({
        queryKey: organizationQueryKeys.show(organizationId),
      })
    },
  })
}

export function useCreateSalesDemoMutation() {
  return useSalesDemoLifecycleMutation(createSalesDemo)
}

export function useRetrySalesDemoMutation() {
  return useSalesDemoLifecycleMutation(retrySalesDemo)
}

export function useExtendSalesDemoMutation() {
  return useSalesDemoLifecycleMutation(extendSalesDemo)
}

export function useDeactivateSalesDemoMutation() {
  return useSalesDemoLifecycleMutation(deactivateSalesDemo)
}

export function useReactivateSalesDemoMutation() {
  return useSalesDemoLifecycleMutation(reactivateSalesDemo)
}

export function useGenerateSalesDemoLoginCredentialsMutation() {
  return useMutation({ mutationFn: generateSalesDemoLoginCredentials })
}
